import { useCallback, useEffect, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { CAMERA_VIDEO_CONSTRAINTS } from './media-quality';
import { createVideoService, type VideoTransport } from './factory';
import type {
  VideoService,
  VisioConnectionState,
  VisioParticipant,
  VisioRole,
} from './video-service';

/**
 * `prejoin` : l'utilisateur choisit micro/caméra AVANT d'entrer (`startJoin`) — la caméra
 * et le micro sont optionnels. Sans `prejoin` (comportement historique du 1:1) on demande
 * les deux dès l'ouverture, avec repli sur l'audio seul puis sur « sans média ».
 */
export type MediaPhase = 'prejoin' | 'requesting' | 'denied' | 'joining' | 'ready';

/** Ce que l'utilisateur veut ouvrir À L'ENTRÉE (le reste s'allume plus tard, à la demande). */
export interface JoinMediaChoice {
  audio: boolean;
  video: boolean;
}

export interface UseVisioRoomResult {
  phase: MediaPhase;
  localStream: MediaStream | null;
  participants: VisioParticipant[];
  connectionState: VisioConnectionState;
  /** L'hôte a TERMINÉ la réunion (vérifié côté serveur) — jamais un simple départ d'un participant. */
  endedByPeer: boolean;
  /** L'hôte NOUS a exclus (la salle continue pour les autres) — distinct d'`endedByPeer`. */
  excludedByHost: boolean;
  micEnabled: boolean;
  cameraEnabled: boolean;
  screenSharing: boolean;
  /** Dernier état de retranscription reçu de l'hôte (piloté exclusivement par le tuteur). */
  remoteTranscriptionActive: boolean;
  /** Nombre de fois où l'hôte a demandé de couper NOTRE micro (déjà coupé) — l'UI le signale. */
  muteRequestCount: number;
  /** Entre dans la salle avec le choix micro/caméra fait à l'écran d'entrée (phase `prejoin`). */
  startJoin: (media: JoinMediaChoice) => void;
  toggleMic: () => void;
  toggleCamera: () => void;
  toggleScreenShare: () => Promise<void>;
  /** Le tuteur seul l'appelle — démarre/arrête la retranscription des participants. */
  sendTranscriptionState: (active: boolean) => void;
  /** L'hôte seul : demande à un participant de couper son micro. */
  requestMute: (userId: string) => void;
  /** L'hôte seul : exclut un participant (ferme sa connexion ; l'appelant fait l'écriture serveur). */
  removeParticipant: (userId: string) => void;
  leave: () => Promise<void>;
  end: () => Promise<void>;
  continueWithoutMedia: () => void;
}

/**
 * Ouvre les médias demandés. Rien demandé → flux vide (on entre en simple spectateur).
 * Refus de la caméra seule → repli sur l'audio ; refus de tout → `null` (écran « denied »).
 */
async function requestLocalStream(want: JoinMediaChoice): Promise<MediaStream | null> {
  if (!want.audio && !want.video) return new MediaStream();
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: want.video ? CAMERA_VIDEO_CONSTRAINTS : false,
      audio: want.audio,
    });
  } catch {
    if (want.video && want.audio) {
      try {
        // Repli audio seul si la caméra est refusée/absente/déjà utilisée.
        return await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        return null;
      }
    }
    return null;
  }
}

export function useVisioRoom(opts: {
  roomId: string;
  me: { userId: string; role: VisioRole; displayName: string };
  /** Client Supabase de l'app appelante (`apps/tutor` ou la route native `apps/app`). */
  db: SupabaseClient;
  supabaseUrl: string;
  /** Id serveur de l'hôte (le tuteur de la salle) — base de confiance des ordres de l'hôte. */
  hostUserId?: string;
  /** `'mesh'` (défaut) ou `'sfu'` (couture non branchée : échec explicite). */
  transport?: VideoTransport;
  /** Écran d'entrée : l'utilisateur choisit micro/caméra avant de rejoindre. */
  prejoin?: boolean;
}): UseVisioRoomResult {
  const [phase, setPhase] = useState<MediaPhase>(opts.prejoin ? 'prejoin' : 'requesting');
  // `null` tant que l'utilisateur n'a pas choisi (prejoin) ; sinon on demande tout d'emblée.
  const [wanted, setWanted] = useState<JoinMediaChoice | null>(
    opts.prejoin ? null : { audio: true, video: true },
  );
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [participants, setParticipants] = useState<VisioParticipant[]>([]);
  const [connectionState, setConnectionState] = useState<VisioConnectionState>('connecting');
  const [endedByPeer, setEndedByPeer] = useState(false);
  const [excludedByHost, setExcludedByHost] = useState(false);
  const [micEnabled, setMicEnabled] = useState(false);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);
  const [remoteTranscriptionActive, setRemoteTranscriptionActive] = useState(false);
  const [muteRequestCount, setMuteRequestCount] = useState(0);
  const serviceRef = useRef<VideoService | null>(null);

  useEffect(() => {
    if (!wanted) return;
    let cancelled = false;
    setPhase('requesting');
    void requestLocalStream(wanted).then((stream) => {
      if (cancelled) {
        stream?.getTracks().forEach((t) => t.stop());
        return;
      }
      if (!stream) {
        setPhase('denied');
        return;
      }
      setMicEnabled(stream.getAudioTracks().some((t) => t.enabled));
      setCameraEnabled(stream.getVideoTracks().some((t) => t.enabled));
      setLocalStream(stream);
      setPhase('joining');
    });
    return () => {
      cancelled = true;
    };
  }, [wanted]);

  const startJoin = useCallback((media: JoinMediaChoice) => {
    setWanted(media);
  }, []);

  const continueWithoutMedia = useCallback(() => {
    setMicEnabled(false);
    setCameraEnabled(false);
    setLocalStream(new MediaStream());
    setPhase('joining');
  }, []);

  useEffect(() => {
    // ⚠️ `phase` n'est PAS une dépendance ici (uniquement lu, jamais dans le
    // tableau de deps ci-dessous) : cet effet ne doit se (re)déclencher que
    // quand `localStream`/`roomId` changent. `joinRoom()` appelle `setPhase
    // ('ready')` à son succès — si `phase` figurait en dépendance, ce SEUL
    // changement re-déclencherait l'effet, dont le cleanup appellerait
    // `leaveRoom()` sur la connexion qu'on vient tout juste d'établir (bug
    // observé : présence posée puis immédiatement retirée par un leaveRoom
    // fantôme, cf. logs `[webrtc] leaveRoom() called` juste après le sync).
    if (phase !== 'joining' || !localStream) return;
    let cancelled = false;

    let service: VideoService;
    try {
      service = createVideoService({
        transport: opts.transport,
        me: opts.me,
        db: opts.db,
        supabaseUrl: opts.supabaseUrl,
        hostUserId: opts.hostUserId,
      });
    } catch (err) {
      // Transport demandé sans implémentation (SFU non branché) : échec explicite.
      console.error('[visio] service vidéo indisponible', err);
      setConnectionState('failed');
      setPhase('ready');
      return;
    }
    serviceRef.current = service;
    const offParticipants = service.onParticipantsChanged((p) => {
      if (!cancelled) setParticipants(p);
    });
    const offConnection = service.onConnectionStateChanged((s) => {
      if (!cancelled) setConnectionState(s);
    });
    const offEnded = service.onRoomEnded(() => {
      if (!cancelled) setEndedByPeer(true);
    });
    const offScreenShare = service.onScreenShareChanged((sharing) => {
      if (!cancelled) setScreenSharing(sharing);
    });
    const offTranscription = service.onTranscriptionStateChanged((active) => {
      if (!cancelled) setRemoteTranscriptionActive(active);
    });
    const offMute = service.onMuteRequested(() => {
      if (cancelled) return;
      setMicEnabled(false);
      setMuteRequestCount((n) => n + 1);
    });
    const offExcluded = service.onExcluded(() => {
      if (!cancelled) setExcludedByHost(true);
    });

    void service
      .joinRoom(opts.roomId, { localStream })
      .then(() => {
        if (!cancelled) setPhase('ready');
      })
      .catch((err: unknown) => {
        console.error('[visio] joinRoom failed', err);
        if (cancelled) return;
        setConnectionState('failed');
        setPhase('ready');
      });

    return () => {
      cancelled = true;
      offParticipants();
      offConnection();
      offEnded();
      offScreenShare();
      offTranscription();
      offMute();
      offExcluded();
      void service.leaveRoom();
      localStream.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStream, opts.roomId]);

  // Le sens du basculement se lit sur l'état RÉEL du service (piste ouverte ET activée), pas
  // sur l'état React : l'ouverture à la demande est asynchrone (permission) et peut échouer.
  const toggleMic = useCallback(() => {
    const svc = serviceRef.current;
    if (!svc) return;
    void svc.setMicEnabled(!svc.getLocalMedia().micOn).then(setMicEnabled);
  }, []);

  const toggleCamera = useCallback(() => {
    const svc = serviceRef.current;
    if (!svc) return;
    void svc.setCameraEnabled(!svc.getLocalMedia().cameraOn).then(setCameraEnabled);
  }, []);

  const toggleScreenShare = useCallback(async () => {
    const svc = serviceRef.current;
    if (!svc) return;
    if (screenSharing) {
      svc.stopScreenShare();
    } else {
      await svc.startScreenShare();
    }
  }, [screenSharing]);

  const sendTranscriptionState = useCallback((active: boolean) => {
    serviceRef.current?.sendTranscriptionState(active);
  }, []);

  const requestMute = useCallback((userId: string) => {
    serviceRef.current?.requestMute(userId);
  }, []);

  const removeParticipant = useCallback((userId: string) => {
    serviceRef.current?.removeParticipant(userId);
  }, []);

  const leave = useCallback(async () => {
    await serviceRef.current?.leaveRoom();
  }, []);

  const end = useCallback(async () => {
    await serviceRef.current?.endRoom();
  }, []);

  return {
    phase,
    localStream,
    participants,
    connectionState,
    endedByPeer,
    excludedByHost,
    micEnabled,
    cameraEnabled,
    screenSharing,
    remoteTranscriptionActive,
    muteRequestCount,
    startJoin,
    toggleMic,
    toggleCamera,
    toggleScreenShare,
    sendTranscriptionState,
    requestMute,
    removeParticipant,
    leave,
    end,
    continueWithoutMedia,
  };
}
